import { useEffect, useState } from "react";
import { Alert, Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";

import { getPersonNumber } from "../../../actions/persons/personnumberget";
import { getContractCount } from "../../../actions/contracts/contractcountget";
import { getContractModelCount } from "../../../actions/contracts/modelcountget";
import { getPerson } from "../../../actions/persons/personget";
import * as PersonFormFields from "../../../common/person/personformfields";
import { getCountryOptions, localized } from "../../../common/utils";
import { activeIntegrationsSelector, authSelector, contractCountSelector, modelContractCountSelector, personNumberSelector, personSelector } from "../../../reducers";
import { PersonSelect } from "../../../common/personselect";
import { countryFilterOption, reactSelectSearchSettings, zIndexFix } from "../../../common/reactselect";
import { useQuery } from "@tanstack/react-query";
import { organizationsQuery } from "../../../api";
import Switch from "../../../common/Switch";

export function ContractGroup1( { state, dispatch, formErrors, onChange }) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();

  const auth = useSelector(authSelector);
  const isBasicLevel = auth.hrLevel !== "premium";
  const basicProps = {};
  if (auth.hrLevel !== "premium") {
    basicProps.className = "premium";
    basicProps.onChange = null;
    basicProps.onClick = e => {
      e.preventDefault();
      reduxDispatch(localized.premiumAlert);
    };
  }

  const [ countryOptions, setCountryOptions ] = useState([]);
  useEffect(() => {
    getCountryOptions(setCountryOptions);
  }, []);

  const organizations = useQuery(organizationsQuery).data;

  const activeintegrations = useSelector(activeIntegrationsSelector);
  const selectedPerson = useSelector(personSelector);
  const selectPerson = newValue => { // ReactSelect
    reduxDispatch( getPerson(newValue.value) ); // personId
  };
  // copy selected person data to contract
  useEffect(() => {
    if (selectedPerson.personId) {
      const company = organizations?.find(org => org.businessIdentifier === selectedPerson.controlDetails?.dimension6); // attach company details to personJSON to save them into contract
      dispatch({ type: "copy_person_details", payload: { ...selectedPerson, company } });
    }
  }, [selectedPerson]);

  const nextPersonNumber = useSelector(personNumberSelector);
  const contractCount = useSelector(contractCountSelector);

  useEffect(() => {
    if (state.newPerson) {
      reduxDispatch(getPersonNumber());
    }
    else if (state.personId && !state.identifier) {
      reduxDispatch(getContractCount(state.person.nr));
    }
  }, [state.personId, state.newPerson]);
  const modelContractCount = useSelector(modelContractCountSelector);
  useEffect(() => {
    if (state.newModel) {
      reduxDispatch(getContractModelCount());
    }
  }, [state.newModel]);

  useEffect(() => {
    if (state.newModel) {
      dispatch({ type:"identifier", payload: `M-${modelContractCount + 1}` });
    }
  }, [modelContractCount]);

  const disabled = state.status === "COMPLETE";
  const personIsNotEmployed = selectedPerson?.employmentDetails?.personStatus === 'NOT_EMPLOYED';

  return (
    <>
      <Row>
        {(state.personId === "" && !state.newModel) &&
          <Form.Group as={Col} xl={2} className="position-relative">
            <Form.Label></Form.Label>
            <Form.Check
              type="checkbox"
              label={t("NewPerson", "Uusi työntekijä")}
              id="newPerson.toggle"
              checked={state.newPerson}
              onChange={onChange}
            />
          </Form.Group>
        }
        { (!state.newPerson && state.personId === "" && !state.newModel) && <PersonSelect selectChange={selectPerson} /> }
      </Row>
      {personIsNotEmployed && (
        <Alert variant="warning">
          {t('PersonNotEmployedContractWarning', 'Henkilö on merkitty ulkopuoliseksi toimijaksi. Työsopimusta ei voida aktivoida ennen kuin ulkopuolinen profiili on päätetty.')}
        </Alert>
      )}
      {(!isBasicLevel) &&
      <Row>
        {(state.personId === "" && !state.newPerson) &&
          <Form.Group as={Col} xl={2}>
            <Form.Label></Form.Label>
            <Form.Check
              type="checkbox"
              label={t("NewModel", "Uusi mallipohja")}
              id="newModel.toggle"
              checked={state.newModel}
              onChange={onChange}
              {...basicProps}
            />
          </Form.Group>
        }
      </Row>
      }
      <Row>
        { (!state.modelName && (state.personId !== "" || state.newPerson)) &&
          <Form.Group as={Col} xl={4} className="position-relative" controlId="nr">
            <PersonFormFields.Nr
              onChange={onChange}
              defaultValue={state.person.nr || nextPersonNumber}
              formErrors={!state.newPerson || formErrors} // do not display error if an existing person is selected
              disabled={disabled || !state.newPerson}
            />
          </Form.Group>
        }
        { (!state.modelName && (state.personId !== "" || state.newPerson)) &&
        <Form.Group as={Col} xl={4} className="position-relative" controlId="identifier">
          <Form.Label>{t("ContractId", "Sopimuksen tunnus")}</Form.Label>
          <Form.Control
            disabled
            value={
              state.identifier || (state.personId // existing contract
                ? `${state.person.nr}-${contractCount + 1}` // new contract for an existing person
                : `${state.person.nr || nextPersonNumber}-1`) // new contract for a new person
            }
          />
        </Form.Group>
        }
        { (state.modelName || (state.personId === "" && state.newModel)) &&
        <Form.Group as={Col} xl={4} className="position-relative" controlId="modelIdentifier">
          <Form.Label>{t("ModelId", "Mallin tunnus")}</Form.Label>
          <Form.Control
            disabled
            value={
              state.identifier || // existing model
                 `M-${modelContractCount + 1}` // new model contract
            }
          />
        </Form.Group>
        }
        { (state.modelName || (state.newModel && state.personId === "")) &&
          <Form.Group as={Col} xl={4} className="position-relative" controlId="modelName">
            <Form.Label>{t("ModelName", "Mallipohjan nimi")}</Form.Label>
            <Form.Control defaultValue={state.modelName ?? ""} onChange={onChange} />
          </Form.Group>
        }
        { (state.personId !== "" || state.newPerson || state.newModel || state.modelName) &&
        <Form.Group as={Col} xl={4} className="position-relative" controlId="language">
          <Form.Label>{t("ContractLanguage", "Työsopimuksen kirjoituskieli")}</Form.Label>
          <Form.Select disabled={disabled} value={state.details.language ?? "fi"} onChange={(evt) => dispatch({ type:"language", payload: evt.target.value })}>
            {localized.languageOptions}
          </Form.Select>
        </Form.Group>
        }
      </Row>
      { (!state.modelName && ((state.personId !== "" || state.newPerson) && !state.newModel)) && <>
        <Row>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="forename">
            <PersonFormFields.Forename
              onChange={onChange}
              defaultValue={state.person.forename}
              formErrors={formErrors}
              surname={state.person.surname}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="surname">
            <PersonFormFields.Surname
              onChange={onChange}
              defaultValue={state.person.surname}
              formErrors={formErrors}
              forename={state.person.forename}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="callingName">
            <PersonFormFields.CallingName
              onChange={onChange}
              defaultValue={state.person.callingName}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="socSec">
            <PersonFormFields.IdentifierCode
              onChange={onChange}
              defaultValue={state.person.socSec}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="gender">
            <PersonFormFields.IdentifierCodeType
              onChange={onChange}
              defaultValue={state.person.gender}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="tel">
            <PersonFormFields.Tel
              onChange={onChange}
              defaultValue={state.person.tel}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="streetAddress">
            <PersonFormFields.StreetAddress
              onChange={onChange}
              defaultValue={state.person.streetAddress}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={2} className="position-relative" controlId="postalCode">
            <PersonFormFields.PostalCode
              onChange={onChange}
              defaultValue={state.person.postalCode}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="postalAddress">
            <PersonFormFields.PostalAddress
              onChange={onChange}
              defaultValue={state.person.postalAddress}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={2} className="position-relative" controlId="country">
            <Form.Label>{t("CountryCode", "Maakoodi")}</Form.Label>
            <ReactSelect
              styles={zIndexFix}
              name="country"
              onChange={(option, element) => dispatch({ type: element.name, payload: option.value })}
              options={countryOptions}
              value={countryOptions.find(option => option.value === state.person.country)}
              isDisabled={disabled}
              {...reactSelectSearchSettings(t)}
              filterOption={countryFilterOption}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="iban">
            <PersonFormFields.IBAN
              onChange={onChange}
              defaultValue={state.person.iban}
              formErrors={formErrors}
              disabled={disabled}
            />
          </Form.Group>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="bic">
            <PersonFormFields.BIC
              onChange={onChange}
              defaultValue={state.person.bic}
              jsonProperty="person.bic"
              disabled={disabled}
            />
          </Form.Group>
        </Row>

        <hr />

        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
          <Switch 
            inline id="personEsalaryDisabled" 
            label={t("NoSalaryExport", "Älä lisää palkkoihin")} 
            onChange={onChange} 
            defaultChecked={state.person.settings?.personEsalaryDisabled} 
          />

          { activeintegrations.tokenPsa &&
            <Switch 
              inline id="personPsaDisabled" 
              label={t("NoPsa", "Älä lisää PSA:han")} 
              onChange={onChange} 
              defaultChecked={state.person.settings?.personPsaDisabled} 
            />
          }
        </div>
      </>}
    </>
  );

}
